import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ExameDetalhes.css';

import { API_URL } from '../config';
import { formatDate, formatTime } from '../utils/formatDate';

function ColetaDomiciliarDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [coleta, setColeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('resumo');

  useEffect(() => {
    fetchColetaDetalhes();
  }, [id]);

  const fetchColetaDetalhes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/coletas-domiciliares/${id}`);
      setColeta(response.data);
    } catch (err) {
      setError('Erro ao carregar dados da coleta: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatarValor = (value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    } else if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    } else if (typeof value === 'string') {
      if (value === 'T' || value === 't') return 'Sim';
      if (value === 'F' || value === 'f') return 'Não';
      return value;
    } else if (typeof value === 'number') {
      return value.toString();
    } else if (value instanceof Date) {
      return value.toLocaleDateString('pt-BR');
    } else {
      return String(value);
    }
  };

  // formatDate e formatTime importados de ../utils/formatDate

  if (loading) {
    return <div className="loading">Carregando detalhes da coleta...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error">{error}</p>
        <button onClick={() => navigate('/coletas-domiciliares')} className="back-button">
          Voltar para Coletas Domiciliares
        </button>
      </div>
    );
  }

  if (!coleta) {
    return <div className="loading">Coleta não encontrada</div>;
  }

  const normalizeId = (str) => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s/g, '-');

  // Mapa de nomes legíveis para campos do banco
  const labelMap = {
    'ID': 'Código', 'DATA': 'Data', 'HORA': 'Hora', 'USUARIO': 'Usuário',
    'PACIENTE': 'Paciente', 'CPF': 'CPF', 'IDENTIDADE': 'Identidade',
    'DATA_NASC': 'Data de Nascimento', 'SEXO': 'Sexo',
    'TELEFONE': 'Telefone', 'CELULAR': 'Celular',
    'NOME_PAI': 'Nome do Pai', 'NOME_MAE': 'Nome da Mãe',
    'ENDERECO': 'Endereço', 'CONVENIO': 'Convênio', 'PLANO': 'Plano',
    'NUM_CARTEIRA': 'Nº Carteira', 'VAL_CARTEIRA': 'Val. Carteira',
    'MEDICO_SOLICITANTE': 'Médico Solicitante',
    'MATERIAL': 'Material', 'OBSERVACAO': 'Observação',
    'TAXA_COLETA': 'Taxa de Coleta',
    'TEM_DOENCA': 'Possui Doença', 'TEM_DOENCA_DESC': 'Descrição da Doença',
    'TOMA_MEDICAMENTO': 'Toma Medicamento', 'TOMA_MEDICAMENTO_DESC': 'Qual Medicamento',
    'TOMOU_REMEDIO': 'Tomou Remédio', 'TOMOU_REMEDIO_DESC': 'Qual Remédio',
  };

  const getLabel = (key) => labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const categorias = {
    'Identificação': ['ID', 'DATA', 'HORA', 'USUARIO'],
    'Dados do Paciente': [
      'PACIENTE', 'CPF', 'IDENTIDADE', 'DATA_NASC', 'SEXO',
      'TELEFONE', 'CELULAR', 'NOME_PAI', 'NOME_MAE'
    ],
    'Endereço': ['ENDERECO'],
    'Convênio': ['CONVENIO', 'PLANO', 'NUM_CARTEIRA', 'VAL_CARTEIRA'],
    'Médico': ['MEDICO_SOLICITANTE'],
    'Material e Observação': ['MATERIAL', 'OBSERVACAO'],
    'Taxa': ['TAXA_COLETA'],
    'Informações de Saúde': [
      'TEM_DOENCA', 'TEM_DOENCA_DESC',
      'TOMA_MEDICAMENTO', 'TOMA_MEDICAMENTO_DESC',
      'TOMOU_REMEDIO', 'TOMOU_REMEDIO_DESC'
    ]
  };

  const camposCategorizados = {};
  const camposUsados = new Set();

  Object.entries(categorias).forEach(([categoria, campos]) => {
    const camposEncontrados = campos.filter(campo =>
      coleta.hasOwnProperty(campo) && !camposUsados.has(campo)
    );
    if (camposEncontrados.length > 0) {
      camposCategorizados[categoria] = camposEncontrados;
      camposEncontrados.forEach(campo => camposUsados.add(campo));
    }
  });

  const camposNaoCategorizados = Object.keys(coleta)
    .filter(key => !camposUsados.has(key));
  if (camposNaoCategorizados.length > 0) {
    camposCategorizados['Outros'] = camposNaoCategorizados;
  }

  // Definir abas disponíveis
  const abas = [
    { id: 'resumo', label: '📋 Resumo', show: true },
    ...Object.keys(camposCategorizados).map(categoria => ({
      id: normalizeId(categoria),
      label: `📄 ${categoria}`,
      show: true
    }))
  ];

  return (
    <div className="exame-detalhes-page">
      <div className="page-header-detalhes">
        <button onClick={() => navigate('/coletas-domiciliares')} className="back-button">
          ← Voltar
        </button>
        <h1>🏠 Detalhes da Coleta Domiciliar</h1>
      </div>

      {/* Sistema de Abas */}
      <div className="detail-tabs">
        {abas.map(aba => (
          <button
            key={aba.id}
            onClick={() => setAbaAtiva(aba.id)}
            className={`detail-tab ${abaAtiva === aba.id ? 'active' : ''}`}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {/* Resumo da Coleta */}
      {abaAtiva === 'resumo' && (
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        border: '2px solid #e5e7eb'
      }}>
        <h2 style={{ marginTop: 0, color: '#333' }}>Resumo da Coleta #{coleta.ID}</h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginTop: '1.5rem'
        }}>
          <div>
            <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
              📅 Data e Hora
            </label>
            <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
              {formatDate(coleta.DATA)} às {formatTime(coleta.HORA)}
            </div>
          </div>

          <div>
            <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
              👤 Paciente
            </label>
            <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
              {coleta.PACIENTE || '-'}
            </div>
          </div>

          <div>
            <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
              📍 Endereço
            </label>
            <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
              {coleta.ENDERECO || '-'}
            </div>
          </div>

          <div>
            <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
              🏥 Convênio
            </label>
            <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
              {coleta.CONVENIO || '-'} {coleta.PLANO && `(${coleta.PLANO})`}
            </div>
          </div>

          <div>
            <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
              👨‍💼 Usuário
            </label>
            <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
              {coleta.USUARIO || '-'}
            </div>
          </div>

          <div>
            <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
              💰 Taxa de Coleta
            </label>
            <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
              {formatarValor(coleta.TAXA_COLETA)}
            </div>
          </div>
        </div>

        {coleta.OBSERVACAO && (
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f9fafb', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
            <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
              📝 Observação
            </label>
            <div style={{ fontSize: '1rem' }}>
              {coleta.OBSERVACAO}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Detalhes Completos - Abas por Categoria */}
      {Object.entries(camposCategorizados).map(([categoria, campos]) => {
        const categoriaId = normalizeId(categoria);
        return abaAtiva === categoriaId ? (
          <div key={categoria} className="detalhes-grid">
            <div className="info-card">
              <h3>📋 {categoria}</h3>
              <div className="info-grid">
                {campos.map((campo) => {
                  let valor = coleta[campo];

                  // Formatação especial para campos de data
                  if (campo === 'DATA' || campo === 'DATA_NASC' || campo === 'VAL_CARTEIRA') {
                    valor = formatDate(valor);
                  } else if (campo === 'HORA') {
                    valor = formatTime(valor);
                  } else {
                    valor = formatarValor(valor);
                  }

                  const isLongText = valor.length > 100;
                  return (
                    <div key={campo} className={isLongText ? 'info-item full-width' : 'info-item'}>
                      <label>{getLabel(campo)}</label>
                      <span className={isLongText ? 'long-text' : ''}>{valor}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null;
      })}
    </div>
  );
}

export default ColetaDomiciliarDetalhes;
